import { Box, Grid, Button, CircularProgress, Typography } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import InputField from "../Reuseables/Fields/InputField";
import { createOrganization} from "../../api/admin";
import { createWorkspaceValidation } from "../../helpers/validationSchemes";
import { useTranslation } from "react-i18next";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";
import YesOrNoField from "../Reuseables/Fields/YesOrNoField";
import MultiSelectField from "../Reuseables/Fields/MultiSelectField";
import SliderField from "../Reuseables/Fields/SliderField";
import { Workspace } from "../../types/workspace";
import { Anonymity, RnD } from "../../types/enums";
import { deviceTypeOptions } from "../../helpers/selectOptions";
import { useMsal } from "@azure/msal-react";
import { CustomAccountInfo } from "../../types/customAccountInfo";


export default function CreateOrganization() {
  const { instance } = useMsal();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();
  const { organizations, setOrganizations } = useOrganizationContext();

  const _onSubmit = async (values: Workspace, actions: FormikHelpers<Workspace>) => {
    try {
      if (values.booleanAnonymity) {
        //Convert boolean to enum value
        values.anonymity = Anonymity.Known;
      } else {
        values.anonymity = Anonymity.Unknown;
      }
      
      let organization = await createOrganization(values);

      setOrganizations([...organizations, organization]);
      showSnackbar(`${organization.name} created.`, "success");
      actions.resetForm();
    } catch (error: unknown) {
      if (error instanceof Error) {
        showSnackbar(error.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    }
  };

  const account = instance.getActiveAccount() as CustomAccountInfo;
  let role = account?.idTokenClaims?.role;

  const initValues: Workspace = {
    organizationId: "",
    tenantName: "",
    anonymity: Anonymity.Unknown,
    availableDeviceTypes: [],
    booleanAnonymity: false,
    rnd: RnD.None,
    diagnostics: false,
  };
  return (
    <Formik
      initialValues={initValues}
      validationSchema={createWorkspaceValidation}
      onSubmit={_onSubmit}
    >
      {(props) => (
        <Form>
          <Grid container mt={4}>
            <Grid item xs />
            <Grid item xl={4}>
              <Box>
                <InputField
                  name="tenantName"
                  label={"Organization name"}
                  placeholder={"placeholders.workspace"}
                />
              </Box>

              <Box mt={4}>
                <MultiSelectField
                  label={"labels.availableDeviceTypes"}
                  name="availableDeviceTypes"
                  data={deviceTypeOptions}
                />
              </Box>
              <Box mt={4}>
                <YesOrNoField
                  name="booleanAnonymity"
                  label={t("labels.anonymisedProjects")}
                />
              </Box>
              <Typography mt={2} color={"red"}>
                Known projects should only be set to Yes for internal test
                workspaces, since its not GDPR compliant.
              </Typography>
              <Box mt={4}>
                <YesOrNoField name="diagnostics" label={"Diagnostic data?"} />
              </Box>
              <Typography mt={2} color={"red"}>
                Diagnostics data should only be used for test workspaces, since
                its not GDPR compliant.
              </Typography>
              <Box mt={4}>
                <SliderField
                  valueLabelDisplay="off"
                  label={t("labels.rnd")}
                  onChange={(value) => {
                    switch (value) {
                      case 0:
                        props.setFieldValue("rnd", RnD.None);
                        break;
                      case 50:
                        props.setFieldValue("rnd", RnD.Internal);
                        break;
                      case 100:
                        props.setFieldValue("rnd", RnD.External);
                        break;
                      default:
                        props.setFieldValue("rnd", RnD.External);
                        break;
                    }
                  }}
                  data={[
                    { value: 0, label: t("labels.none") },
                    { value: 50, label: t("labels.internal") },
                    { value: 100, label: t("labels.external") },
                  ]}
                  defaultValue={0}
                />
              </Box>
              <Typography mt={2} color={"red"}>
                NONE means no wizard will be shown in the APP.
              </Typography>
              <Box mt={5}>
                {props.isSubmitting ? (
                  <Button variant="contained" disabled>
                    <CircularProgress size={20} />
                    {t("buttons.submitting")}
                  </Button>
                ) : (
                  <Button
                    disabled={props.isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    {t("buttons.create")}
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs />
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
